import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { supportAPI } from '../../../services';
import {
  LifeBuoy,
  X,
  Upload,
  Search,
  UserPlus,
  Paperclip,
  Clock,
  RefreshCw,
  CheckCircle,
  XCircle,
  Star,
  UsersRound
} from 'lucide-react';
import { C } from './theme';

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800'
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  pending: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800'
};

const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', pending: 'Pending', resolved: 'Resolved', closed: 'Closed' };
const NEXT_ALLOWED = { open: ['in_progress', 'closed'], in_progress: ['pending', 'resolved', 'closed'], pending: ['in_progress', 'closed'], resolved: ['closed'], closed: [] };

const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

// DigiHR-side inbox: superadmin sees/responds to/closes platform-bug tickets raised by any
// client company's HR Admin. Mirrors ContactSubmissionsPage.jsx's stats/filter/list layout,
// but sourced from supportAPI.platform (authenticated client admins) rather than the public
// contact form.
const PlatformSupportInbox = () => {
  const { user } = useAuth();
  // superadmin keeps full access; platform_staff needs the platformSupport.update permission
  // to change status/assign - anyone who can reach this page (platformSupport.read) can still
  // view and comment.
  const canUpdate = user?.role === 'superadmin' || user?.platformPermissions?.platformSupport?.update === true;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' });
  const [selected, setSelected] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentInternal, setCommentInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [showPerformance, setShowPerformance] = useState(false);
  const [performance, setPerformance] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        search: filters.search || undefined
      };
      const res = await supportAPI.platform.getAll(params);
      setTickets(Array.isArray(res?.data) ? res.data : []);
    } catch {
      showToast('Failed to load tickets', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const stats = tickets.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  const toggleStaffPerformance = async () => {
    const next = !showPerformance;
    setShowPerformance(next);
    if (next && performance.length === 0) {
      try {
        setPerformanceLoading(true);
        const res = await supportAPI.platform.getStaffPerformance();
        setPerformance(Array.isArray(res?.data) ? res.data : []);
      } catch {
        showToast('Failed to load staff performance', 'error');
      } finally {
        setPerformanceLoading(false);
      }
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await supportAPI.platform.getOne(id);
      setSelected(res.data);
    } catch {
      showToast('Failed to load ticket', 'error');
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setCommentText('');
    setCommentInternal(false);
    setFiles([]);
  };

  const refreshSelected = async () => {
    const res = await supportAPI.platform.getOne(selected._id);
    setSelected(res.data);
  };

  const handleStatusChange = async (status) => {
    try {
      const res = await supportAPI.platform.update(selected._id, { status });
      setSelected(res.data);
      showToast('Status updated');
      load();
    } catch (err) {
      showToast(err?.message || 'Failed to update status', 'error');
    }
  };

  const handleAssignToMe = async () => {
    try {
      const res = await supportAPI.platform.update(selected._id, { assignee: user._id || user.id });
      setSelected(res.data);
      showToast('Ticket assigned to you');
      load();
    } catch (err) {
      showToast(err?.message || 'Failed to assign ticket', 'error');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      await supportAPI.platform.addComment(selected._id, { text: commentText.trim(), is_internal: commentInternal });
      setCommentText('');
      setCommentInternal(false);
      await refreshSelected();
    } catch (err) {
      showToast(err?.message || 'Failed to add comment', 'error');
    }
  };

  const handleUploadFiles = async () => {
    if (files.length === 0) return;
    try {
      await supportAPI.platform.uploadAttachments(selected._id, files);
      setFiles([]);
      await refreshSelected();
      showToast('Files uploaded');
    } catch (err) {
      showToast(err?.message || 'Failed to upload files', 'error');
    }
  };

  const slaProgress = (ticket) => {
    const created = new Date(ticket.createdAt).getTime();
    const due = new Date(ticket.sla_due_at).getTime();
    const now = Date.now();
    const pct = Math.min(100, Math.max(0, ((now - created) / (due - created)) * 100));
    const breached = now > due && !['resolved', 'closed'].includes(ticket.status);
    return { pct, breached };
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast.msg}
        </div>
      )}

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-[26px] font-bold leading-tight" style={{ color: C.ink }}>
            Platform Support
          </h1>
          <p className="text-[13px] mt-1" style={{ color: C.textMuted }}>
            Bugs and issues reported by client HR admins across all companies
          </p>
        </div>
        <button
          onClick={toggleStaffPerformance}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <UsersRound className="w-4 h-4" /> {showPerformance ? 'Hide' : 'Show'} Staff Performance
        </button>
      </div>

      {showPerformance && (
        <div className="bg-white rounded-lg shadow p-4 mb-6 overflow-x-auto">
          {performanceLoading ? (
            <div className="p-4 text-center text-gray-400">Loading...</div>
          ) : performance.length === 0 ? (
            <div className="p-4 text-center text-gray-400">No assigned tickets yet</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">Staff</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-500">Assigned</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-500">Resolved</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-500">Closed</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-500">Avg Rating</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-500">Avg Resolution (hrs)</th>
                </tr>
              </thead>
              <tbody>
                {performance.map((p) => (
                  <tr key={p.staffId} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900">{p.name}</td>
                    <td className="text-center py-2 px-3">{p.assigned}</td>
                    <td className="text-center py-2 px-3">{p.resolved}</td>
                    <td className="text-center py-2 px-3">{p.closed}</td>
                    <td className="text-center py-2 px-3">
                      <span className="inline-flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" /> {p.avgRating || '—'}
                      </span>
                    </td>
                    <td className="text-center py-2 px-3">{p.avgResolutionHours || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-600">Open</p><p className="text-2xl font-bold text-gray-900">{stats.open || 0}</p></div>
            <Clock className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-600">In Progress / Pending</p><p className="text-2xl font-bold text-gray-900">{(stats.in_progress || 0) + (stats.pending || 0)}</p></div>
            <RefreshCw className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-600">Resolved</p><p className="text-2xl font-bold text-gray-900">{stats.resolved || 0}</p></div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-gray-400">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-600">Closed</p><p className="text-2xl font-bold text-gray-900">{stats.closed || 0}</p></div>
            <XCircle className="w-8 h-8 text-gray-400" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search subject/description..."
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : tickets.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <LifeBuoy className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            No platform tickets found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SLA</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tickets.map((t) => {
                  const sla = slaProgress(t);
                  return (
                    <tr key={t._id} onClick={() => openDetail(t._id)} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{t.ticket_id}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{t.company_id?.name || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">{t.subject}</td>
                      <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span></td>
                      <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span></td>
                      <td className="px-6 py-4 w-32">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full transition-all ${sla.breached ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${sla.pct}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={closeDetail} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl h-full overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selected.ticket_id}</h2>
                <p className="text-sm text-gray-500">{selected.company_id?.name} · {selected.subject}</p>
              </div>
              <button onClick={closeDetail} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${PRIORITY_COLORS[selected.priority]}`}>{selected.priority}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selected.status]}`}>{STATUS_LABELS[selected.status]}</span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{selected.category}</span>
              </div>

              {(() => {
                const sla = slaProgress(selected);
                return (
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>SLA</span>
                      <span>{sla.breached ? 'Breached' : `Due ${formatDateTime(selected.sla_due_at)}`}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${sla.breached ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${sla.pct}%` }} />
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg mt-1 text-sm">{selected.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><label className="text-xs font-medium text-gray-500">Raised By</label><p className="text-gray-900">{selected.raised_by?.name || '—'}</p></div>
                <div><label className="text-xs font-medium text-gray-500">Assignee</label><p className="text-gray-900">{selected.assignee?.name || 'Unassigned'}</p></div>
              </div>

              {selected.status !== 'closed' && canUpdate && (
                <div className="pt-3 border-t border-gray-100 space-y-3">
                  {NEXT_ALLOWED[selected.status].length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 block mb-2">Update Status</label>
                      <div className="flex gap-2 flex-wrap">
                        {NEXT_ALLOWED[selected.status].map((s) => (
                          <button key={s} onClick={() => handleStatusChange(s)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700">
                            {STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {!selected.assignee && (
                    <button onClick={handleAssignToMe} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                      <UserPlus className="w-3.5 h-3.5" /> Assign to Me
                    </button>
                  )}
                </div>
              )}

              <div className="pt-3 border-t border-gray-100">
                <label className="text-xs font-medium text-gray-500 block mb-2">Attachments ({selected.attachments?.length || 0}/5)</label>
                <div className="space-y-1 mb-2">
                  {(selected.attachments || []).map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                      <Paperclip className="w-3.5 h-3.5" /> {a.filename}
                    </a>
                  ))}
                </div>
                {(selected.attachments?.length || 0) < 5 && (
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-500 flex-1">
                      <Upload className="w-4 h-4 text-gray-400" />
                      {files.length > 0 ? `${files.length} file(s) selected` : 'Choose files'}
                      <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        className="hidden"
                        onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5 - (selected.attachments?.length || 0)))}
                      />
                    </label>
                    <button onClick={handleUploadFiles} disabled={files.length === 0} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50">
                      Upload
                    </button>
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-gray-100">
                <label className="text-xs font-medium text-gray-500 block mb-2">Comments</label>
                <div className="space-y-3 mb-3 max-h-64 overflow-y-auto">
                  {(selected.comments || []).length === 0 ? (
                    <p className="text-sm text-gray-400">No comments yet</p>
                  ) : selected.comments.map((c) => (
                    <div key={c._id} className={`p-3 rounded-lg text-sm ${c.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{c.author_id?.name || c.role_label}</span>
                        <span className="text-xs text-gray-400">{formatDateTime(c.created_at)}</span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{c.text}</p>
                      {c.is_internal && <span className="text-xs text-yellow-700 font-medium">Internal note</span>}
                    </div>
                  ))}
                </div>
                {selected.status !== 'closed' && (
                  <div className="space-y-2">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={2}
                      placeholder="Add a comment..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input type="checkbox" checked={commentInternal} onChange={(e) => setCommentInternal(e.target.checked)} />
                        Internal note (not visible to client)
                      </label>
                      <button onClick={handleAddComment} disabled={!commentText.trim()} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                        Post
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSupportInbox;
